import React, { useState, useEffect } from 'react';
import { profileService, VoteHistory } from '../../services/profile.service';

interface VotingHistoryTabProps {
  userId: string;
  isOwnProfile: boolean;
}

export function VotingHistoryTab({ userId, isOwnProfile }: VotingHistoryTabProps) {
  const [votes, setVotes] = useState<VoteHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVotingHistory();
  }, [userId]);

  const loadVotingHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await profileService.getUserVotingHistory(userId);
      setVotes(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar el historial de votaciones');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-yellow-800 dark:text-yellow-200 text-sm">
          {error}
        </p>
      </div>
    );
  }

  if (!isOwnProfile) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-gray-600 dark:text-gray-400">
          El historial de votaciones es privado
        </p>
      </div>
    );
  }

  if (votes.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-gray-600 dark:text-gray-400">
          Aún no has votado en ninguna encuesta
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {votes.map((vote) => (
        <div
          key={vote.id}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                {vote.poll.title}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                {vote.option.contestant && vote.option.contestant.photo_url && (
                  <img
                    src={vote.option.contestant.photo_url}
                    alt={vote.option.contestant.full_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {vote.option.contestant?.full_name || vote.option.text || 'Opción eliminada'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {new Date(vote.created_at).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  vote.poll.status === 'active'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    : vote.poll.status === 'closed'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                }`}
              >
                {vote.poll.status === 'active' ? 'Activa' : vote.poll.status === 'closed' ? 'Cerrada' : 'Borrador'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
